import { getCurrentInstance, onUnmounted } from "vue";

export type LogoutGuard = (silent: boolean) => boolean | Promise<boolean>;

const logoutGuards = new Set<LogoutGuard>();

/**
 * Registers a guard that runs before the user is signed out, letting a page
 * block the logout (e.g. to confirm unsaved changes). Returning false aborts
 * the sign out. The guard receives `silent`, which is true when it should
 * perform its cleanup (e.g. saving a draft) without showing any confirmation
 * dialog, such as on a session timeout. Automatically unregistered when the
 * calling component unmounts.
 */
export function registerLogoutGuard(guard: LogoutGuard) {
  logoutGuards.add(guard);
  const unregisterLogoutGuard = () => {
    logoutGuards.delete(guard);
  };
  if (getCurrentInstance()) {
    onUnmounted(unregisterLogoutGuard);
  }
  return unregisterLogoutGuard;
}

/**
 * Runs every registered logout guard. Resolves false as soon as one of them
 * declines, so the sign out can be aborted. When `silent` is true, guards are
 * expected to perform cleanup without prompting the user, and their return
 * value is not used to abort the sign out.
 */
export async function confirmLogout(silent = false): Promise<boolean> {
  for (const guard of Array.from(logoutGuards)) {
    try {
      if (!(await guard(silent)) && !silent) {
        return false;
      }
    } catch (error) {
      console.error("Logout guard failed:", error);
    }
  }
  return true;
}
